import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { type BaseRequest, type BaseResponse } from '../models'
import { paginate } from '../utils'
import { type CreateCustomIntegrationPayload, type CustomIntegration, type UpdateCustomIntegrationPayload } from '../../models/CustomIntegration'

const _BASE_URL = import.meta.env.REACT_APP_INTEGRATIONS_SERVICE_API_BASE_URL!

interface CustomIntegrationRequest extends BaseRequest {
  customIntegrationId: string
}

interface CreateCustomIntegrationRequest extends BaseRequest {
  payload: CreateCustomIntegrationPayload
}

interface UpdateCustomIntegrationRequest extends CustomIntegrationRequest {
  payload: UpdateCustomIntegrationPayload
}

const CUSTOM_INTEGRATION_TAG = 'CustomIntegration'
const CUSTOM_INTEGRATION_LIST_TAG = { type: CUSTOM_INTEGRATION_TAG, id: 'LIST' } as const

export const customIntegrationApi = createApi({
  reducerPath: 'customIntegrationApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: [CUSTOM_INTEGRATION_TAG],
  endpoints: (builder) => ({
    listCustomIntegrations: builder.query<BaseResponse<CustomIntegration[]>, BaseRequest>({
      async queryFn({ accountId }, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<CustomIntegration>({ url: `/accounts/${accountId}/custom-integrations/` }, fetchWithBQ)
      },
      providesTags: [CUSTOM_INTEGRATION_LIST_TAG],
    }),
    getCustomIntegration: builder.query<CustomIntegration, CustomIntegrationRequest>({
      query: ({ accountId, customIntegrationId }) => `/accounts/${accountId}/custom-integrations/${customIntegrationId}`,
      transformResponse: (response: BaseResponse<CustomIntegration>) => response.data,
      providesTags: (_result, _error, { customIntegrationId }) => [{ type: CUSTOM_INTEGRATION_TAG, id: customIntegrationId }],
    }),
    createCustomIntegration: builder.mutation<CustomIntegration, CreateCustomIntegrationRequest>({
      query: ({ accountId, payload }) => ({
        url: `/accounts/${accountId}/custom-integrations/`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: BaseResponse<CustomIntegration>) => response.data,
      invalidatesTags: [CUSTOM_INTEGRATION_LIST_TAG],
    }),
    updateCustomIntegration: builder.mutation<CustomIntegration, UpdateCustomIntegrationRequest>({
      query: ({ accountId, customIntegrationId, payload }) => ({
        url: `/accounts/${accountId}/custom-integrations/${customIntegrationId}`,
        method: 'PATCH',
        body: payload,
      }),
      transformResponse: (response: BaseResponse<CustomIntegration>) => response.data,
      invalidatesTags: (_result, _error, { customIntegrationId }) => [CUSTOM_INTEGRATION_LIST_TAG, { type: CUSTOM_INTEGRATION_TAG, id: customIntegrationId }],
    }),
    refreshCustomIntegrationTools: builder.mutation<CustomIntegration, CustomIntegrationRequest>({
      query: ({ accountId, customIntegrationId }) => ({
        url: `/accounts/${accountId}/custom-integrations/${customIntegrationId}/tools/refresh`,
        method: 'POST',
      }),
      transformResponse: (response: BaseResponse<CustomIntegration>) => response.data,
      invalidatesTags: (_result, _error, { customIntegrationId }) => [{ type: CUSTOM_INTEGRATION_TAG, id: customIntegrationId }],
    }),
    deleteCustomIntegration: builder.mutation<void, CustomIntegrationRequest>({
      query: ({ accountId, customIntegrationId }) => ({
        url: `/accounts/${accountId}/custom-integrations/${customIntegrationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [CUSTOM_INTEGRATION_LIST_TAG],
    }),
  }),
})

export const {
  useListCustomIntegrationsQuery,
  useGetCustomIntegrationQuery,
  useCreateCustomIntegrationMutation,
  useUpdateCustomIntegrationMutation,
  useRefreshCustomIntegrationToolsMutation,
  useDeleteCustomIntegrationMutation,
} = customIntegrationApi
